import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  alpha,
  Badge,
  Box,
  Button,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Tab,
  Tabs,
  Typography,
  useTheme,
} from '@mui/material';
import { Close, ForumOutlined, Mail, Work } from '@mui/icons-material';
import { useQueryClient } from 'react-query';

import { NotificationContext, NotificationType } from '@bandido/shared/types';
import { NotificationResponseDto, PagedResponse } from '@bandido/shared/dtos';

import { getTimeSinceDate } from '../../helper';
import { useGetNotifications, useMarkAllNotificationsAsRead } from '../../queries';
import { useNavigate } from 'react-router-dom';
import { routes } from '@frontend/router/routes';
import { client } from '../../services';
import { QueryKeys } from '../../types';
import { useAuth } from '@frontend/features/auth/hooks/useAuth';
import { useInbox } from '@frontend/features/inbox/InboxContext';
import { useInboxItems } from '@frontend/features/inbox/queries';
import {
  notificationCenterBadgeCount,
  NotificationCenterTab,
  unreadConversationFilters,
  unreadConversationItems,
} from './model';

export const notificationTypeIcon: Partial<Record<NotificationType, JSX.Element>> = {
  [NotificationType.CASE_STATUS_MODIFIED]: <Work />,
  [NotificationType.CASE_NOTE_ADDED]: <Work />,
  [NotificationType.CASE_CLIENT_REPLIED]: <Mail />,
  [NotificationType.CASE_EMAIL_ADDED]: <Mail />,
  [NotificationType.CASE_UNASSIGNED_ME]: <Work />,
  [NotificationType.CASE_ASSIGNED_TO_ME]: <Work />,
};
export interface INotification {
  id: string;
  notificationType: NotificationType;
  payload: {
    details?: Record<string, any>;
  };
  context: NotificationContext;
  user?: string | null;
  isRead: boolean;
  createdDate: string;
}

export const notificationRoute = (notificationType: NotificationType, caseId: string) =>
  (notificationType === NotificationType.CASE_CLIENT_REPLIED ? routes.dashboard.inboxCase : routes.dashboard.caseView)
    .replace(':id', caseId)
    .replace(':caseId', caseId);

interface Props {
  open?: boolean;
  notificationIconRef: React.MutableRefObject<HTMLDivElement | null>;
  onClose: () => void;
}

export const NotificationsDrawer = ({ open, onClose, notificationIconRef }: Props) => {
  const drawerRef = useRef<HTMLDivElement | null>(null);
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { myProfile } = useAuth();
  const { hasInboxAccess, unreadCaseCount } = useInbox();
  const [activeTab, setActiveTab] = useState<NotificationCenterTab>(hasInboxAccess ? 'conversations' : 'activity');

  const { data } = useGetNotifications({
    currentPage: 0,
    pageSize: 20,
    search: '',
  });
  const notificationsQueryKey = [QueryKeys.getNotifications, 0, 20, ''];
  const unreadCount = data?.items?.filter((notification) => !notification.isRead).length ?? 0;
  const conversationsQuery = useInboxItems(myProfile?.id, unreadConversationFilters, Boolean(open && hasInboxAccess));
  const conversations = useMemo(
    () => unreadConversationItems(conversationsQuery.data?.pages),
    [conversationsQuery.data],
  );
  const hasAdditionalConversations =
    Boolean(conversationsQuery.hasNextPage) ||
    notificationCenterBadgeCount(hasInboxAccess, unreadCaseCount, myProfile?.notifications) > conversations.length;

  const markAllAsReadMutation = useMarkAllNotificationsAsRead({
    onSuccess: () => {
      const oldData = queryClient.getQueryData<PagedResponse<NotificationResponseDto>>(notificationsQueryKey);
      if (oldData) {
        queryClient.setQueryData(notificationsQueryKey, {
          ...oldData,
          items: oldData.items.map((notification) => ({
            ...notification,
            isRead: true,
          })),
        });
      }
      queryClient.invalidateQueries({ queryKey: [QueryKeys.getNotifications] });
      queryClient.invalidateQueries({ queryKey: [QueryKeys.getMyUserData] });
    },
  });

  const handleClickOutside = (event: MouseEvent) => {
    event.preventDefault();
    const existeIconRef = !(notificationIconRef.current && !notificationIconRef.current.contains(event.target as Node));
    const existeDrawer = !(drawerRef.current && !drawerRef.current.contains(event.target as Node));

    if (!existeDrawer && !existeIconRef) {
      onClose();
    }
  };

  const handleMarkAsRead = async (notifcationId: string) => {
    try {
      await client.post(`notifications/${notifcationId}/read`);
    } catch (e) {
      // Aca no uso el snackbar adrede, no me peleen en el PR
      console.error(e);
    }
  };

  const handleMarkAllAsRead = () => {
    if (markAllAsReadMutation.isLoading || unreadCount === 0) {
      return;
    }
    markAllAsReadMutation.mutate();
  };

  // TODO: esto va a ir modificando pq vamos a tener status no relacionados a casos
  const handleClickNotification = (notifcationId: string, notificationType: NotificationType, caseId: string) => () => {
    try {
      handleMarkAsRead(notifcationId);
      navigate(notificationRoute(notificationType, caseId));
    } catch (error) {
      // Aca no uso el snackbar adrede, no me peleen en el PR
      console.error(error);
    }
    onClose();
  };

  const handleClickConversation = (caseId: number) => () => {
    navigate(routes.dashboard.inboxCase.replace(':caseId', String(caseId)));
    onClose();
  };

  const handleViewAllConversations = () => {
    navigate(`${routes.dashboard.inbox}?unread=1`);
    onClose();
  };

  useEffect(() => {
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open]);

  useEffect(() => {
    if (open) setActiveTab(hasInboxAccess ? 'conversations' : 'activity');
  }, [hasInboxAccess, open]);

  return (
    <Drawer
      ref={drawerRef}
      anchor="right"
      open={open}
      onClose={onClose}
      hideBackdrop
      sx={{
        width: { xs: '100vw', sm: 390 },
        '& .MuiDrawer-paper': {
          width: { xs: '100vw', sm: 390 },
          minWidth: { xs: 0, sm: 390 },
          height: { xs: '100dvh', sm: 'calc(100vh - 64px)' },
          zIndex: 1100,
          marginTop: { xs: 0, sm: '64px' },
        },
      }}
    >
      <Box minHeight={56} paddingX={2} paddingY={1} display="flex" alignItems="center" gap={1}>
        <Typography variant="h6">{t('notifications.title')}</Typography>
        <IconButton aria-label={t('notifications.close')} onClick={onClose} sx={{ ml: 'auto' }}>
          <Close />
        </IconButton>
      </Box>
      {hasInboxAccess && (
        <Tabs
          value={activeTab}
          onChange={(_event, value: NotificationCenterTab) => setActiveTab(value)}
          variant="fullWidth"
          aria-label={t('notifications.tabs.label')}
        >
          <Tab
            value="conversations"
            label={t('notifications.tabs.conversations')}
            icon={
              <Badge badgeContent={unreadCaseCount} color="error" max={99}>
                <ForumOutlined />
              </Badge>
            }
            iconPosition="start"
          />
          <Tab value="activity" label={t('notifications.tabs.activity')} />
        </Tabs>
      )}

      {activeTab === 'conversations' && hasInboxAccess ? (
        <Box display="flex" flexDirection="column" minHeight={0} flex={1} overflow="auto">
          {conversationsQuery.isLoading && (
            <Stack alignItems="center" justifyContent="center" minHeight={180} gap={1}>
              <CircularProgress size={24} />
              <Typography variant="body2">{t('notifications.conversations.loading')}</Typography>
            </Stack>
          )}
          {conversationsQuery.isError && (
            <Stack alignItems="center" justifyContent="center" minHeight={180} gap={1} px={3} textAlign="center">
              <Typography fontWeight={600}>{t('notifications.conversations.error')}</Typography>
              <Button onClick={() => conversationsQuery.refetch()}>{t('common.retry')}</Button>
            </Stack>
          )}
          {!conversationsQuery.isLoading && !conversationsQuery.isError && conversations.length === 0 && (
            <Stack alignItems="center" justifyContent="center" minHeight={180} gap={1} px={3} textAlign="center">
              <ForumOutlined color="disabled" sx={{ fontSize: 40 }} />
              <Typography fontWeight={600}>{t('notifications.conversations.emptyTitle')}</Typography>
              <Typography variant="body2" color="text.secondary">
                {t('notifications.conversations.emptyDescription')}
              </Typography>
            </Stack>
          )}
          {conversations.length > 0 && (
            <List disablePadding aria-label={t('notifications.conversations.listLabel')}>
              <Divider />
              {conversations.map((conversation) => (
                <Fragment key={conversation.caseId}>
                  <ListItemButton onClick={handleClickConversation(conversation.caseId)} alignItems="flex-start">
                    <ListItemIcon>
                      <Badge badgeContent={conversation.unreadCount} color="error" max={99}>
                        <ForumOutlined color="primary" />
                      </Badge>
                    </ListItemIcon>
                    <ListItemText
                      primary={conversation.clientName || conversation.caseName}
                      secondary={
                        <>
                          <Typography component="span" variant="body2" color="text.primary" display="block" noWrap>
                            {conversation.lastActivity?.text ||
                              t(`inbox.channels.${conversation.lastActivity?.kind || 'unknown'}`)}
                          </Typography>
                          {t('notifications.conversations.caseNumber', { caseId: conversation.caseId })}
                          {' · '}
                          {getTimeSinceDate(conversation.lastActivity?.occurredAt || '')}
                        </>
                      }
                    />
                  </ListItemButton>
                  <Divider />
                </Fragment>
              ))}
            </List>
          )}
          {hasAdditionalConversations && (
            <Button onClick={handleViewAllConversations} sx={{ m: 2 }}>
              {t('notifications.conversations.viewAll')}
            </Button>
          )}
        </Box>
      ) : (
        <Box minHeight={0} flex={1} overflow="auto">
          <Box display="flex" alignItems="center" px={2}>
            <Typography variant="caption">{t('notifications.recents')}</Typography>
            <Button
              color="primary"
              onClick={handleMarkAllAsRead}
              disabled={markAllAsReadMutation.isLoading || unreadCount === 0}
              sx={{ ml: 'auto' }}
            >
              {t('notifications.clean')}
            </Button>
          </Box>
          <List>
            <Divider />
            {data?.items.map((notification) => (
              <Fragment key={notification.id}>
                <ListItemButton
                  selected={!notification.isRead}
                  onClick={handleClickNotification(
                    notification.id,
                    notification.notificationType,
                    notification.payload?.caseId,
                  )}
                >
                  <ListItemIcon>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: alpha(theme.palette[notification.context].light, 0.2),
                        border: `2px solid ${theme.palette[notification.context].dark}`,
                        color: theme.palette[notification.context].dark,
                        borderRadius: '50%',
                        width: '40px',
                        height: '40px',
                      }}
                    >
                      {notificationTypeIcon[notification.notificationType]}
                    </div>
                  </ListItemIcon>
                  <Box>
                    <Box display="flex">
                      <Typography variant="subtitle1" fontWeight={500} color={theme.palette[notification.context].dark}>
                        {t(`notifications.${notification.notificationType}.title`)}
                      </Typography>
                    </Box>
                    <ListItemText
                      sx={{ '& b': { fontWeight: '500', fontSize: 'inherit' } }}
                      primary={
                        <span
                          dangerouslySetInnerHTML={{
                            __html: t(`notifications.${notification.notificationType}.message`, notification.payload),
                          }}
                        />
                      }
                      secondary={getTimeSinceDate(notification.createdAt)}
                    />
                  </Box>
                </ListItemButton>
                <Divider />
              </Fragment>
            ))}
          </List>
        </Box>
      )}
    </Drawer>
  );
};
